// The distiller: parsed Letterboxd CSV rows -> DistilledProfile (§8).
// Pure and deterministic so it can be unit-tested with fixture CSVs and
// reused identically in the worker and the seed script.

import type {
  DistilledProfile,
  FilmEntry,
  ProfileAggregates,
  ProfileCounts,
} from "./types.ts";

/** A raw CSV row from any Letterboxd export file (header -> value). */
export type CsvRow = Record<string, string>;

export interface ExportTables {
  watched?: CsvRow[];
  ratings?: CsvRow[];
  watchlist?: CsvRow[];
  diary?: CsvRow[];
  /** Letterboxd account username (from profile.csv), for deep links. */
  username?: string;
}

// Letterboxd column names drift; look them up case-insensitively and tolerate
// a few known aliases.
const pick = (row: CsvRow, ...names: string[]): string | undefined => {
  const keys = Object.keys(row);
  for (const name of names) {
    const hit = keys.find((k) => k.trim().toLowerCase() === name.toLowerCase());
    if (hit && row[hit] != null && row[hit] !== "") return row[hit];
  }
  return undefined;
};

const yearOf = (row: CsvRow): number | undefined => {
  const y = pick(row, "Year");
  if (!y) return undefined;
  const n = parseInt(y, 10);
  return Number.isFinite(n) ? n : undefined;
};

/**
 * A film's canonical identity: the (title, year) tuple, normalized. We do NOT
 * key on the Letterboxd URI, because Letterboxd's diary.csv "Letterboxd URI" is
 * the per-log permalink (e.g. boxd.it/16TmxX), NOT the film's URI (boxd.it/jj4y)
 * — so a watched+logged film would otherwise become two entries, inflating
 * counts and duplicating cross-recs/canon. Title|year collapses those, and also
 * matches the same film across users even when their URIs differ.
 */
export const canonicalKey = (title: string, year?: number): string =>
  `${(title || "").trim().toLowerCase()}|${year ?? ""}`;

const keyOf = (row: CsvRow): { key: string; title: string; year?: number } | null => {
  const title = pick(row, "Name", "Title");
  if (!title) return null;
  const year = yearOf(row);
  return { key: canonicalKey(title, year), title: title.trim(), year };
};

/**
 * Collapse an already-distilled film map onto the canonical (title, year) key.
 * Used defensively at blend time so profiles stored under the old URI-based
 * scheme (which could hold duplicates) are deduped before any math runs.
 */
export function canonicalizeFilms(films: Record<string, FilmEntry>): Record<string, FilmEntry> {
  const out: Record<string, FilmEntry> = {};
  for (const e of Object.values(films)) {
    const key = canonicalKey(e.t, e.y);
    const cur = out[key];
    if (!cur) {
      out[key] = { t: e.t, s: e.s, ...(e.y != null ? { y: e.y } : {}), ...(e.r != null ? { r: e.r } : {}) };
      continue;
    }
    for (const flag of ["w", "r", "l"] as const) if (e.s.includes(flag)) cur.s = composeFlags(cur.s, flag);
    if (cur.y == null && e.y != null) cur.y = e.y;
    if (e.r != null) cur.r = cur.r != null ? Math.max(cur.r, e.r) : e.r;
  }
  return out;
}

const parseRating = (v: string | undefined): number | undefined => {
  if (v == null || v === "") return undefined;
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return undefined;
  // Clamp to the 0.5–5.0 half-step domain.
  const clamped = Math.max(0.5, Math.min(5, n));
  return Math.round(clamped * 2) / 2;
};

const decadeOf = (year?: number): string | undefined => {
  if (!year || year < 1870 || year > 2100) return undefined;
  return String(Math.floor(year / 10) * 10);
};

// "2026-07-01", "2026-07-01 12:00", or similar -> "YYYY-MM".
const monthOf = (dateStr: string | undefined): string | undefined => {
  if (!dateStr) return undefined;
  const m = dateStr.match(/(\d{4})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}` : undefined;
};

const composeFlags = (existing: string, add: "w" | "r" | "l"): string =>
  existing.includes(add) ? existing : existing + add;

export interface DistillOptions {
  /** Override the derived export date (used by fixtures for determinism). */
  exportedAt?: string;
}

/**
 * Reduce the four export tables to a single distilled profile.
 * Films are keyed on the canonical (title, year) tuple, so rewatches, diary
 * logs, and cross-file duplicates all collapse onto one entry per film.
 */
export function distill(tables: ExportTables, opts: DistillOptions = {}): DistilledProfile {
  const films: Record<string, FilmEntry> = {};
  const ratingHist: Record<string, number> = {};
  const byDecade: Record<string, number> = {};
  const loggedByMonth: Record<string, number> = {};
  let latestDate = "";

  const ensure = (row: CsvRow): FilmEntry | null => {
    const id = keyOf(row);
    if (!id) return null;
    let entry = films[id.key];
    if (!entry) {
      entry = { t: id.title, s: "" };
      if (id.year != null) entry.y = id.year;
      films[id.key] = entry;
    }
    return entry;
  };

  // watched.csv — the base set.
  for (const row of tables.watched ?? []) {
    const e = ensure(row);
    if (e) e.s = composeFlags(e.s, "w");
  }

  // ratings.csv — implies watched, adds a rating.
  for (const row of tables.ratings ?? []) {
    const e = ensure(row);
    if (!e) continue;
    e.s = composeFlags(e.s, "w");
    const r = parseRating(pick(row, "Rating"));
    if (r != null) {
      e.r = r;
      e.s = composeFlags(e.s, "r");
    }
  }

  // watchlist.csv — the "want to see" set; not necessarily watched.
  for (const row of tables.watchlist ?? []) {
    const e = ensure(row);
    if (e) e.s = composeFlags(e.s, "l");
  }

  // diary.csv — read client-side only; reduced to monthly counts (§8.1).
  // Also implies watched + may carry a rating.
  for (const row of tables.diary ?? []) {
    const e = ensure(row);
    if (e) {
      e.s = composeFlags(e.s, "w");
      const r = parseRating(pick(row, "Rating"));
      if (r != null && e.r == null) {
        e.r = r;
        e.s = composeFlags(e.s, "r");
      }
    }
    const watchedDate = pick(row, "Watched Date", "Date");
    const month = monthOf(watchedDate);
    if (month) loggedByMonth[month] = (loggedByMonth[month] ?? 0) + 1;
    if (watchedDate && watchedDate > latestDate) latestDate = watchedDate;
  }

  // Derive aggregates from the final film set.
  let watched = 0;
  let rated = 0;
  let watchlist = 0;
  for (const e of Object.values(films)) {
    if (e.s.includes("w")) watched++;
    if (e.s.includes("l")) watchlist++;
    if (e.r != null && e.s.includes("r")) {
      rated++;
      const key = e.r % 1 === 0 ? String(e.r) : e.r.toFixed(1);
      ratingHist[key] = (ratingHist[key] ?? 0) + 1;
    }
    const dec = decadeOf(e.y);
    // Only count films the user has actually watched toward the decade chart.
    if (dec && e.s.includes("w")) byDecade[dec] = (byDecade[dec] ?? 0) + 1;
  }

  const counts: ProfileCounts = { watched, rated, watchlist };
  const aggregates: ProfileAggregates = { ratingHist, byDecade, loggedByMonth };

  const exportedAt =
    opts.exportedAt ??
    (latestDate ? latestDate.slice(0, 10) : new Date().toISOString().slice(0, 10));

  return { v: 1, exportedAt, letterboxdUsername: tables.username, films, aggregates, counts };
}

/** Convenience: does this profile satisfy the server's not-empty constraint? */
export function isNonEmptyProfile(p: DistilledProfile): boolean {
  return (
    p.films != null &&
    typeof p.films === "object" &&
    Object.keys(p.films).length > 0 &&
    p.counts?.watched != null
  );
}
